import { Flex, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { getUserMeThunk, postSchedulerCreateRoute } from "tm-shared/api";
import { RouteDetails } from "tm-shared/types";

import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import { Loading } from "@/components/Loading";
import { PrimaryText } from "@/components/Typography/Styled";
import { MANAGER_AGENT_VOCABULARY } from "@/constants/domain/managerAgentVocabulary";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

import { RouteEditorEdit } from "./RouteEditorEdit";

export const RouteEditorMyRoute = () => {
    const dispatch = useAppDispatch();
    const creatingRef = useRef(false);

    const {
        id: userId,
        role,
        firstName,
        lastName,
        team,
    } = useAppSelector((s) => s.user);

    const existingRoute = team?.route;
    const hasRoute = !!existingRoute?.uuid;

    const [creating, setCreating] = useState(!hasRoute);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (hasRoute || creatingRef.current) return;

        creatingRef.current = true;

        const createRoute = async () => {
            try {
                setCreating(true);
                const roleSlug = MANAGER_AGENT_VOCABULARY[role] ?? role;
                const roleLabel = roleSlug?.toUpperCase() ?? "KAM";
                const routeName = `${roleLabel}: ${lastName} ${firstName}`;

                await postSchedulerCreateRoute({
                    ownerId: userId,
                    name: routeName,
                    agentId: userId,
                    schedules: [],
                    roleSlug,
                    mainRegionId: team?.mainRegionId ?? "",
                    partnerId: "alcoline",
                });

                await dispatch(getUserMeThunk()).unwrap();
            } catch {
                setError("Не вдалося створити маршрут. Спробуйте ще раз.");
            } finally {
                setCreating(false);
            }
        };

        createRoute();
    }, []);

    if (creating) {
        return (
            <>
                <Header />
                <MainContainer>
                    <Flex
                        vertical
                        align="center"
                        justify="center"
                        style={{ height: "100dvh", gap: 16 }}
                    >
                        <Spin size="large" />
                        <PrimaryText>Маршрут створюється...</PrimaryText>
                    </Flex>
                </MainContainer>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Header />
                <MainContainer>
                    <Flex
                        vertical
                        align="center"
                        justify="center"
                        style={{ height: "100dvh", gap: 8 }}
                    >
                        <PrimaryText $textColor="error">{error}</PrimaryText>
                    </Flex>
                </MainContainer>
            </>
        );
    }

    const currentRoute = team?.route;

    if (!currentRoute?.uuid) {
        return (
            <>
                <Header />
                <MainContainer>
                    <Loading />
                </MainContainer>
            </>
        );
    }

    const fullRoute: RouteDetails = {
        uuid: currentRoute.uuid,
        agentId: currentRoute.agentId,
        ownerId: currentRoute.ownerId,
        name: currentRoute.name,
        description: currentRoute.description ?? "",
        id: 0,
        agentFullName: `${firstName} ${lastName}`,
        ownerFullName: `${firstName} ${lastName}`,
        roleSlug: role,
        marketCount: 0,
        createdAt: "",
        updatedAt: "",
    };

    return (
        <RouteEditorEdit currentRoute={fullRoute} routeId={fullRoute.uuid} />
    );
};
